import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Search } from 'react-feather';

import { cn } from '~lib/utils/style';

import { Button } from '~ui/base/button';

interface SearchInputProps {
  initialQuery: string;
  onSubmit: (query: string) => void;
  className?: string;
}

/**
 * Search input + primary submit button on a single row. The search input is
 * the visual anchor of the page — give it room to breathe (DESIGN.md §7).
 */
function SearchInput({ initialQuery, onSubmit, className }: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(initialQuery);

  // Reflect external query changes (e.g. browser back/forward) into the input.
  useEffect(() => {
    setDraft(initialQuery);
  }, [initialQuery]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    inputRef.current?.blur();
  }

  return (
    <form
      role="search"
      onSubmit={handleSubmit}
      className={cn('flex w-full items-stretch gap-x-3', className)}
    >
      <label className="relative flex-1">
        <span className="sr-only">Search datasets</span>
        <Search
          aria-hidden
          className="pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          ref={inputRef}
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Search FAIR² datasets…"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={cn(
            'block h-14 w-full rounded-full border border-border bg-input pl-14 pr-5 text-base text-foreground placeholder:text-muted-foreground',
            'focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
            'transition-colors',
          )}
        />
      </label>
      <Button type="submit" size="lg" className="h-14 px-7">
        Search
      </Button>
    </form>
  );
}

export { SearchInput };
